import React, { useRef, useState, useCallback, useEffect } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Avatar } from "primereact/avatar";
import { Skeleton } from "primereact/skeleton";
import { Dialog } from "primereact/dialog";
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";
import "react-virtualized/styles.css";
import moment from "moment";
import GalleryTemplate from "../ArticleTemplate/GalleryTemplate";
import { useArticle } from "../../hooks/useArticle";
import "./BusinessFeeds.css";
import { Divider } from "primereact/divider";

const BusinessFeeds = ({
  posts = [],
  loading = false,
  hasMore = false,
  onLoadMore = () => {},
  onRefresh = () => {},
}) => {
  const listRef = useRef(null);
  const loadingRef = useRef(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [displayModal, setDisplayModal] = useState(false);
  const [articleData, setArticleData] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const { getArticleById } = useArticle();

  // Create a cache for cell measurements
  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 200, // Default height for initial render
    })
  );

  // Clear cache when posts change
  useEffect(() => {
    cache.current.clearAll();
  }, [posts]);

  // Format timestamp to local time
  const formatToLocalTime = (timestamp) => {
    if (!timestamp) return "";
    
    try {
      // Handle Firestore timestamp with seconds
      if (timestamp.seconds) {
        return moment.unix(timestamp.seconds).local().format('MMM D, YYYY h:mm A');
      }
      
      // Handle epoch time (in milliseconds)
      if (typeof timestamp === 'number') {
        // Check if it's in seconds (10 digits) or milliseconds (13 digits)
        const date = timestamp.toString().length === 10 
          ? moment.unix(timestamp)
          : moment(timestamp);
        return date.local().format('MMM D, YYYY h:mm A');
      }
      
      // Handle ISO strings or other date formats
      return moment(timestamp).local().format('MMM D, YYYY h:mm A');
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  };

  // Format timestamp to relative time (e.g., "2 hours ago")
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Just now";

    try {
      let date;
      
      if (timestamp.seconds) {
        // Firestore timestamp
        date = moment.unix(timestamp.seconds);
      } else if (typeof timestamp === 'number') {
        // Epoch time
        date = timestamp.toString().length === 10 
          ? moment.unix(timestamp)
          : moment(timestamp);
      } else if (typeof timestamp === 'string' && timestamp.includes(' at ')) {
        // Handle "Month DD, YYYY at hh:mm:ss A UTC+X" format
        const datePart = timestamp.split(' at ')[0];
        date = moment(datePart, 'MMMM D, YYYY');
        
        // If the date is invalid or in the future, try parsing as is
        if (!date.isValid() || date.isAfter(moment())) {
          date = moment(timestamp);
        }
      } else {
        // ISO string or other date format
        date = moment(timestamp);
      }
      
      // If the date is still invalid, return a default message
      if (!date.isValid()) {
        console.warn('Invalid date format:', timestamp);
        return 'Recently';
      }
      
      return date.fromNow();
    } catch (error) {
      console.error('Error formatting relative time:', error, 'Timestamp:', timestamp);
      return 'Recently';
    }
  };

  // Format date for article modal
  const formatArticleDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.seconds
      ? moment.unix(timestamp.seconds)
      : moment(timestamp);
    return date.format("MMMM D, YYYY");
  };

  // Handle post click
  const handlePostClick = async (post) => {
    try {
      setArticleLoading(true);
      setSelectedPost(post);

      // Fetch the full article data
      if (post.id) {
        const article = await getArticleById(post.id);
        setArticleData(article);
      } else {
        // Fallback to post data if no articleId is available
        setArticleData({
          title: post.title,
          byline: post.byline || "Author",
          updatedAt: moment(post.updatedAt || post.createdAt).unix(),
          thumbnail: post.imageUrl ? { url: post.imageUrl } : null,
          sections: post.content
            ? [
                {
                  type: "text",
                  content: post.content,
                },
              ]
            : [],
        });
      }

      setDisplayModal(true);
    } catch (error) {
      console.error("Error fetching article:", error);
      // Fallback to post data if there's an error
      setArticleData({
        title: post.title,
        byline: post.byline || "Author",
        updatedAt: moment(post.updatedAt || post.createdAt).unix(),
        thumbnail: post.imageUrl ? { url: post.imageUrl } : null,
        sections: post.content
          ? [
              {
                type: "text",
                content: post.content,
              },
            ]
          : [],
      });
      setDisplayModal(true);
    } finally {
      setArticleLoading(false);
    }
  };

  // Close modal handler
  const handleCloseModal = () => {
    setDisplayModal(false);
    setSelectedPost(null);
    setArticleData(null);
  };

  // Handle modal hide
  const handleHideModal = () => {
    setDisplayModal(false);
    setSelectedPost(null);
  };

  // Handle refresh
  const handleRefresh = () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    onRefresh().finally(() => {
      loadingRef.current = false; // Reset loading ref when refresh is done
    });
  };

  // Skeleton card component
  const SkeletonCard = () => (
    <div className="p-3">
      <Card className="w-full relative">
        <div className="flex align-items-center mb-3">
          <Skeleton shape="circle" size="3rem" className="mr-3"></Skeleton>
          <div className="flex-1">
            <Skeleton width="10rem" height="1.5rem" className="mb-2"></Skeleton>
            <Skeleton width="5rem" height="1rem"></Skeleton>
          </div>
        </div>
        <Skeleton width="100%" height="1.5rem" className="mb-2"></Skeleton>
        <Skeleton width="100%" height="1.5rem" className="mb-2"></Skeleton>
        <Skeleton width="50%" height="1.5rem" className="mb-3"></Skeleton>
        <div className="flex justify-content-between">
          <Skeleton width="6rem" height="2rem"></Skeleton>
          <Skeleton width="8rem" height="2rem"></Skeleton>
          <Skeleton width="6rem" height="2rem"></Skeleton>
        </div>
      </Card>
    </div>
  );

  // Row renderer with CellMeasurer
  const rowRenderer = ({ index, key, parent, style }) => {
    const post = getItems()[index];

    if (post === "no-more") {
      return null;
    }

    if (!post) {
      return (
        <div key={key} style={style} className="p-3">
          <SkeletonCard />
        </div>
      );
    }

    return (
      <CellMeasurer
        key={key}
        cache={cache.current}
        parent={parent}
        columnIndex={0}
        rowIndex={index}
      >
        {({ measure }) => (
          <div style={style} className="p-3">
            <div className="relative">
              <Card
                className="w-full relative cursor-pointer hover:shadow-3 transition-shadow transition-duration-150"
                onClick={() => handlePostClick(post)}
              >
                <div className="flex align-items-center mb-3">
                  <Avatar
                    image={
                      post.businessLogo ||
                      "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM2YzY3NmYiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwYXRoIGQ9Ik0xOSAyMXYtM2MwLTEuNjU3LTEuMzQzLTMtMy0zSDhjLTEuNjU3IDAtMyAxLjM0My0zIDN2MyIvPjxjaXJjbGUgY3g9IjEyIiBjeT0iNyIgcj0iNCIvPjwvc3ZnPg=="
                    }
                    shape="circle"
                    className="mr-2"
                    size="large"
                    imageAlt={post.businessName}
                  />
                  <div>
                    <div className="font-bold">
                      {post.businessName || "Business"}
                    </div>
                    <div className="text-sm text-500">
                      {formatTimestamp(post._raw?.createdAt)}
                    </div>
                  </div>
                </div>
                <Divider />
                {post.thumbnail?.url && (
                  <img
                    src={post.thumbnail.url}
                    alt={post.thumbnail.alt || "Post thumbnail"}
                    className="w-full border-round"
                    style={{ maxHeight: "300px", objectFit: "contain" }}
                    onLoad={measure} // Re-measure when image loads
                  />
                )}
                <Divider />
                <h1
                  className="mb-2"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {post.postTitle}
                </h1>
                <p
                  className="m-0"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: "1.5",
                    maxHeight: "3em",
                  }}
                >
                  {post.postContent}
                </p>
              </Card>
            </div>
          </div>
        )}
      </CellMeasurer>
    );
  };

  // Get items for VirtualScroller
  const getItems = () => {
    if (!hasMore && posts.length > 0) {
      return [...posts, "no-more"];
    }
    return [...posts];
  };

  return (
    <div className="business-feeds h-full w-full flex flex-column">
      <div className="flex justify-content-between align-items-center p-3 surface-border">
        <h2 className="text-xl font-bold m-0">Business Feed</h2>
        <Button
          icon="pi pi-refresh"
          className="p-button-rounded p-button-text"
          onClick={handleRefresh}
          disabled={loading}
          tooltip="Refresh feed"
          tooltipOptions={{ position: "left" }}
        />
      </div>
      <Divider />

      <div
        className="flex-grow-1 w-full"
        style={{ height: "calc(100vh - 120px)" }}
      >
        {loading && posts.length === 0 ? (
          // Show skeleton cards when loading initial data
          <>
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : posts.length === 0 ? (
          // Show empty state when no posts
          <div
            className="flex flex-column align-items-center justify-content-center p-6 text-center"
            style={{ height: "100%" }}
          >
            <i className="pi pi-inbox text-6xl text-400 mb-3"></i>
            <h3 className="text-700 mb-2">No posts yet</h3>
            <p className="text-500 mb-4">
              Be the first to share something with your customers!
            </p>
            <Button
              label="Create your first post"
              icon="pi pi-plus"
              className="p-button-outlined"
              onClick={() => {}}
            />
          </div>
        ) : (
          // Show actual content when data is loaded
          <div style={{ height: "100%", width: "100%" }}>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  ref={listRef}
                  width={width}
                  height={height}
                  rowCount={getItems().length}
                  deferredMeasurementCache={cache.current}
                  rowHeight={cache.current.rowHeight}
                  rowRenderer={rowRenderer}
                  onScroll={({ scrollTop }) => {
                    const scrollPosition = height + scrollTop;
                    const totalHeight =
                      getItems().length * cache.current.defaultHeight;

                    if (
                      scrollPosition >= totalHeight - 500 &&
                      hasMore &&
                      !loading
                    ) {
                      onLoadMore();
                    }
                  }}
                  style={{
                    outline: "none",
                    overflowX: "hidden",
                  }}
                />
              )}
            </AutoSizer>
          </div>
        )}

        {loading && posts.length > 0 && (
          <div className="flex justify-content-center p-4">
            <i className="pi pi-spin pi-spinner text-2xl"></i>
          </div>
        )}
      </div>

      {/* Article Modal */}
      <Dialog
        header={selectedPost?.title || "Article"}
        visible={displayModal}
        onHide={handleCloseModal}
        style={{ width: "90vw", maxWidth: "1200px" }}
        contentStyle={{ height: "80vh" }}
        className="p-fluid"
        maximizable
      >
        {articleLoading ? (
          <div
            className="flex justify-content-center align-items-center"
            style={{ height: "100%" }}
          >
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "2rem" }}
            ></i>
          </div>
        ) : articleData ? (
          <div style={{ height: "100%", overflowY: "auto" }}>
            <GalleryTemplate article={articleData} />
          </div>
        ) : (
          <div className="text-center p-5">
            <i
              className="pi pi-exclamation-triangle"
              style={{ fontSize: "3rem", color: "var(--yellow-500)" }}
            ></i>
            <h3>Article Not Available</h3>
            <p>Could not load the article content. Please try again later.</p>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default BusinessFeeds;
